import { Box, CircularProgress, Collapse, Grid, IconButton, Typography, Paper, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MultiDatePicker from '../../../../components/documented/MultiDatePicker/MultiDatePicker';
import { DateGroupType } from '../../../../model/active/dateGroupType';
import { PassiveParameterTypeEnum } from '../../../../model/passive/enum/passiveParameterTypeEnum';
import { AlertAnnotation } from '../../../../model/passive/alertMoment';
import { IProcessedTemperatureData } from '../../../../model/passive/temperatureData';
import { getPassiveParameterData } from '../../../../redux/actions/dailyParameterActions';
import { useAppThunkDispatch } from '../../../../redux/configureStore';
import { processedTemperatureData } from '../../../../redux/selectors/dailyParametersSelectors';
import { selectedMetricDate } from '../../../../redux/selectors/dailyMetricsSelectors';
import LeftFootChart from './charts/LeftFootChart';
import RightFootChart from './charts/RightFootChart';
import TemperatureDifferenceChart from './charts/TemperatureDifferenceChart';

interface Props {
  patientId?: string;
  selectedDateRange?: [Date | null, Date | null];
  alertAnnotations?: AlertAnnotation[];
  onAlertAnnotationClick?: (alertId: number) => void;
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const DatePickerContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginBottom: '16px',
  gap: '16px',
});

const ResetZoomButton = styled(Button)({});

const ChartPaper = styled(Paper)({
  padding: '20px',
  marginBottom: '24px',
  position: 'relative',
});

const ChartContainer = styled(Box)({
  height: '350px',
  marginTop: '16px',
  position: 'relative',
});

const LoadingContainer = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
});


const TemperatureDetailContent: React.FC<Props> = ({
  patientId,
  selectedDateRange: externalDateRange,
  alertAnnotations = [],
  onAlertAnnotationClick,
}) => {
  const dispatch = useAppThunkDispatch();
  const { t } = useTranslation('dailyParameters');

  const temperatureData = useSelector(processedTemperatureData);
  const selectedDailyMetricDate = useSelector(selectedMetricDate);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processedData, setProcessedData] = useState<
    IProcessedTemperatureData[]
  >([]);

  // Refs for all three charts to enable synchronized zooming
  const leftFootChartRef = useRef<any>(null);
  const rightFootChartRef = useRef<any>(null);
  const differenceChartRef = useRef<any>(null);

  // Internal date range state - initialize based on selected date from Redux or today's date
  const [leftExpanded, setLeftExpanded] = useState(false);
  const [rightExpanded, setRightExpanded] = useState(false);

  const [internalDateRange, setInternalDateRange] = useState<[Date | null, Date | null]>(() => {
    if (selectedDailyMetricDate) {
      const selectedDate = new Date(selectedDailyMetricDate);
      return [selectedDate, selectedDate];
    }
    const today = new Date();
    return [today, today];
  });

  // Sync internal date range when the Redux selected date changes (e.g. header date picker)
  useEffect(() => {
    if (selectedDailyMetricDate) {
      const selectedDate = new Date(selectedDailyMetricDate);
      setInternalDateRange([selectedDate, selectedDate]);
    }
  }, [selectedDailyMetricDate]);

  // Use external date range if provided, otherwise use internal
  const selectedDateRange = externalDateRange || internalDateRange;

  // Synchronized zoom handler
  const handleZoomChange = useCallback((sourceChart: any) => {
    if (!sourceChart) return;

    const charts = [leftFootChartRef.current, rightFootChartRef.current, differenceChartRef.current];

    // Get the zoom state from the source chart
    const xScale = sourceChart.scales?.x;
    if (!xScale) return;

    const { min, max } = xScale;

    // Apply the same zoom to all other charts with animation
    charts.forEach((chart) => {
      if (chart && chart !== sourceChart) {
        chart.zoomScale('x', { min, max }, 'default');
      }
    });
  }, []);

  // Reset zoom on all charts
  const handleResetZoom = useCallback(() => {
    [leftFootChartRef.current, rightFootChartRef.current, differenceChartRef.current].forEach((chart) => {
      if (chart) {
        chart.resetZoom();
      }
    });
  }, []);

  useEffect(() => {
    if (patientId && selectedDateRange[0] && selectedDateRange[1]) {
      setIsLoading(true);
      dispatch(
        getPassiveParameterData(
          patientId,
          selectedDateRange[0],
          selectedDateRange[1],
          DateGroupType.Day,
          PassiveParameterTypeEnum.Temperature
        )
      ).finally(() => {
        setIsLoading(false);
      });
    }
  }, [dispatch, patientId, selectedDateRange]);

  useEffect(() => {
    const sortedData = [...temperatureData].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    setProcessedData(sortedData);
  }, [temperatureData]);

  return (
    <Root>
      {/* Date Picker and Reset Zoom Button */}
      <DatePickerContainer>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', marginRight: 'auto' }}>
          <ResetZoomButton
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleResetZoom}
          >
            {t('temperature.resetZoom')}
          </ResetZoomButton>
          <Typography variant="caption" color="textSecondary">
            <Trans
              t={t}
              i18nKey="temperature.panHint"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Box>
        <MultiDatePicker
          isRangePicker={true}
          selectedRange={internalDateRange}
          setSelectedRange={setInternalDateRange}
        />
      </DatePickerContainer>

      {isLoading && (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      )}

      {processedData.length === 0 && !isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography variant="body1" color="textSecondary">
            {t('temperature.noRangeData')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Temperature Difference Chart */}
          <Grid size={12}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" gutterBottom>
                {t('temperature.differenceTitle')}
              </Typography>
              <ChartContainer>
                <TemperatureDifferenceChart
                  data={processedData}
                  selectedDateRange={selectedDateRange}
                  chartRef={differenceChartRef}
                  onZoomChange={handleZoomChange}
                  alertAnnotations={alertAnnotations}
                  onAlertAnnotationClick={onAlertAnnotationClick}
                />
              </ChartContainer>
            </ChartPaper>
          </Grid>

          {/* Left Foot Chart */}
          <Grid size={12}>
            <ChartPaper elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('temperature.leftFootTitle')}</Typography>
                <IconButton size="small" aria-label={t('temperature.expandLeft')} onClick={() => setLeftExpanded(v => !v)}>
                  <ExpandMoreIcon sx={{ transform: leftExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </IconButton>
              </Box>
              <Collapse in={leftExpanded}>
                <ChartContainer>
                  <LeftFootChart
                    data={processedData}
                    selectedDateRange={selectedDateRange}
                    chartRef={leftFootChartRef}
                    onZoomChange={handleZoomChange}
                    alertAnnotations={alertAnnotations}
                    onAlertAnnotationClick={onAlertAnnotationClick}
                    />
                </ChartContainer>
              </Collapse>
            </ChartPaper>
          </Grid>

          {/* Right Foot Chart */}
          <Grid size={12}>
            <ChartPaper elevation={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('temperature.rightFootTitle')}</Typography>
                <IconButton size="small" aria-label={t('temperature.expandRight')} onClick={() => setRightExpanded(v => !v)}>
                  <ExpandMoreIcon sx={{ transform: rightExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </IconButton>
              </Box>
              <Collapse in={rightExpanded}>
                <ChartContainer>
                  <RightFootChart
                    data={processedData}
                    selectedDateRange={selectedDateRange}
                    chartRef={rightFootChartRef}
                    onZoomChange={handleZoomChange}
                    alertAnnotations={alertAnnotations}
                    onAlertAnnotationClick={onAlertAnnotationClick}
                    />
                </ChartContainer>
              </Collapse>
            </ChartPaper>
          </Grid>
        </Grid>
      )}

    </Root>
  );
};

export default TemperatureDetailContent;
